import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { BarChart3 } from 'lucide-react';

interface Keyword {
  id: number;
  keyword_text: string;
  volume: number;
  competition: string;
  da_score?: number;
  low_cpc?: number;
  high_cpc?: number;
}

interface VolumeDifficultyMatrixProps {
  keywords: Keyword[];
  isLoading?: boolean;
  selectedKeyword?: Keyword | null;
}

const VolumeDifficultyMatrix: React.FC<VolumeDifficultyMatrixProps> = ({ keywords, isLoading = false, selectedKeyword = null }) => {
  // Debug logging
  
  // Check if any keywords have da_score data
  const keywordsWithoutDA = keywords.filter(keyword => keyword.da_score === null || keyword.da_score === undefined);
  
  // Transform data for the scatter plot
  const chartData = keywords
    .filter(keyword => {
      const hasRequiredData = keyword.volume && keyword.competition && keyword.da_score !== null;
      return hasRequiredData;
    })
    .map(keyword => ({
      x: keyword.volume,
      y: keyword.da_score || 0,
      keyword: keyword.keyword_text,
      competition: keyword.competition,
      volume: keyword.volume,
      da_score: keyword.da_score,
    }))
    .sort((a, b) => a.x - b.x);

  const config = {
    volume: {
      label: "Volume",
      color: "hsl(var(--chart-1))",
    },
    difficulty: {
      label: "Difficulty",
      color: "hsl(var(--chart-2))",
    },
  };

  if (isLoading) {
    return (
      <Card className="px-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Volume vs Difficulty</span>
          </CardTitle>
          <CardDescription>
            Distribution of keywords by volume and difficulty
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card className="px-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Volume vs Difficulty</span>
          </CardTitle>
          <CardDescription>
            Distribution of keywords by volume and difficulty
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center">
            <div className="text-center">
              <div className="text-muted-foreground mb-2">No difficulty data available</div>
              <div className="text-sm text-muted-foreground mb-4">
                {keywordsWithoutDA.length > 0  
                  ? `⚠️ Need to analyze ${keywordsWithoutDA.length} keywords to get difficulty data`
                  : '⚠️ Keywords need volume and difficulty data to display'
                }
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="px-0">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <BarChart3 className="h-5 w-5" />
          <span>Volume vs Difficulty</span>
        </CardTitle>
        <CardDescription>
          Distribution of keywords by volume and difficulty
        </CardDescription>
      </CardHeader>
              <CardContent className="p-4">
        {/* Keyword Details Widget */}
        
        
                <div className="h-32 w-full">
          <ChartContainer config={config}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart
                style={{
                  marginLeft: '-20px',
                }}
                data={chartData}
                margin={{
                  top: 10,
                  right: 20,
                  bottom: 30,
                  left: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Volume"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toString();
                  }}
                  label={{ value: 'Volume', position: 'insideBottom', offset: -10 }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name="Difficulty"
                  domain={[0, 100]}
                />
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-sm">
                          <div className="space-y-2">
                            <div className="font-medium text-foreground">
                              {data.keyword}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-muted-foreground">Volume:</span>
                                <span className="ml-1 font-medium">
                                  {data.volume?.toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Difficulty:</span>
                                <span className="ml-1 font-medium">
                                  {data.da_score}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Competition:</span>
                                <span className="ml-1 font-medium">
                                  {data.competition}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter
                  dataKey="y"
                  fill="hsl(var(--primary))"
                  stroke="hsl(var(--primary))"
                  strokeWidth={1}
                  opacity={0.7}
                  name="Difficulty Score"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default VolumeDifficultyMatrix; 